/**
 * GraphQL Hooks for Photos
 *
 * Apollo Client hooks for photo operations with automatic
 * transformation to REST-compatible types for component compatibility.
 *
 * Feature: 063-graphql-frontend
 * Tasks: T018-T021
 */

import { useQuery } from '@apollo/client';
import {
  GET_PHOTOS,
  GET_PHOTO,
  GET_PHOTO_BY_SLUG,
} from '../queries';
import { mapGraphQLPhotoToREST } from '../mappers';
import type { GraphQLPhoto, PaginatedResult } from '../types';
import type { Photo } from '../../types/photo';
import logger from '../../utils/logger';

interface GetPhotosData {
  photos: PaginatedResult<GraphQLPhoto> & {
    hasMore: boolean;
    limit: number;
    offset: number;
  };
}

interface GetPhotoData {
  photo: GraphQLPhoto;
}

interface GetPhotoBySlugData {
  userPhotoBySlug: GraphQLPhoto;
}

/**
 * Query: List all photos for authenticated user with infinite scroll support
 *
 * Returns photos with loadMore function for infinite scroll.
 * @param limit - Number of photos per page (default: 20)
 *
 * Note: Kept at 20 to avoid CDN rate limiting when loading thumbnails.
 * Infinite scroll will load more as user scrolls.
 */
export function usePhotosGraphQL(limit: number = 20) {
  const { data, loading, error, refetch, fetchMore } = useQuery<GetPhotosData>(GET_PHOTOS, {
    variables: { limit, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });

  /**
   * Load more photos using cursor-based pagination.
   * Automatically merges new results with existing data.
   * Handles errors gracefully with logging.
   */
  const loadMore = async () => {
    if (!data?.photos.hasMore) {
      return;
    }

    const currentOffset = data.photos.items.length;

    try {
      await fetchMore({
        variables: {
          limit,
          offset: currentOffset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const newItems = fetchMoreResult.photos.items;
          const mergedItems = [...prev.photos.items, ...newItems];

          // If no new items were returned, stop loading to prevent infinite loop
          const hasMore = newItems.length > 0 && mergedItems.length < fetchMoreResult.photos.totalCount;

          return {
            photos: {
              ...fetchMoreResult.photos,
              items: mergedItems,
              hasMore,
            },
          };
        },
      });
    } catch (err) {
      logger.error('usePhotosGraphQL', 'Failed to load more photos', err);
    }
  };

  // Transform to component-compatible format
  const photos = data?.photos.items.map(mapGraphQLPhotoToREST) ?? [];

  return {
    photos,
    totalCount: data?.photos.totalCount ?? 0,
    hasMore: data?.photos.hasMore ?? false,
    loading,
    error,
    refetch,
    loadMore,
  };
}

/**
 * Query: Get single photo by ID
 *
 * Returns photo data in REST-compatible format.
 */
export function usePhotoByIdGraphQL(photoId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetPhotoData>(GET_PHOTO, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: photoId || 'skip-placeholder' },
    skip: !photoId,
    fetchPolicy: 'cache-and-network',
  });

  // Transform to REST-compatible format
  const photo: Photo | undefined = data?.photo
    ? mapGraphQLPhotoToREST(data.photo)
    : undefined;

  return {
    data: photo,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Query: Get photo by username and slug
 *
 * Returns photo data in REST-compatible format.
 */
export function usePhotoBySlugGraphQL(username: string | undefined, photoSlug: string | undefined) {
  const shouldSkip = !username || !photoSlug;
  const { data, loading, error, refetch } = useQuery<GetPhotoBySlugData>(GET_PHOTO_BY_SLUG, {
    // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: {
      username: username || 'skip-placeholder',
      photoSlug: photoSlug || 'skip-placeholder'
    },
    skip: shouldSkip,
    fetchPolicy: 'cache-and-network',
  });

  // Transform to REST-compatible format
  const photo: Photo | undefined = data?.userPhotoBySlug
    ? mapGraphQLPhotoToREST(data.userPhotoBySlug)
    : undefined;

  return {
    data: photo,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Query: Get photo metadata (same as usePhotoByIdGraphQL but focused on metadata)
 *
 * Returns photo with metadata in REST-compatible format.
 */
export function usePhotoMetadataGraphQL(photoId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetPhotoData>(GET_PHOTO, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: photoId || 'skip-placeholder' },
    skip: !photoId,
    fetchPolicy: 'cache-and-network',
  });

  // Transform to REST-compatible format
  const photo: Photo | undefined = data?.photo
    ? mapGraphQLPhotoToREST(data.photo)
    : undefined;

  return {
    data: photo,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}
